/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {TemplateArgument} from '../shared/documents/documents.interface';
import {SignatureDataEntry, SignatureMethodEnum} from '../shared/files/files.model';
import {FieldType} from '../shared/form-generator/fields/fields.interfaces';
import {TreeNode} from '../shared/ui';
import {TreeItem} from '../shared/ui/tree-multi-select/treeview.component';

export interface ETemplate {
  id?: number;
  name: string;
  folder: string;
  locale: string;
  gloRegId?: number;
  cliRegId?: number;
  cliRegName?: string;
  availability?: string[];
  createdBy?: string;
  updatedDate?: string;
  transId?: string;
  status?: string;
  value?: string;
  arguments?: TemplateArgument[];
  eTemplateAutoSave?: boolean;
  signatureData?: SignatureDataEntry[];
}

export type PlaceholderMatch = 'EXACT' | 'LIKELY' | 'UNSURE' | 'NONE';

export type PlaceholderNoMatchReason = 'NOT_A_DATA_FIELD' | 'NO_MATCH' | 'AMBIGUOUS';

/** MEMORY when the proposal repeats the user's own earlier choice, AIKB when it comes from the catalogue alone. */
export type PlaceholderSource = 'MEMORY' | 'AIKB';

export interface ResolvedField {
  fieldName: string;
  transId: string;
}

export interface ResolvedPlaceholder {
  token: string;
  fields: ResolvedField[];
  match: PlaceholderMatch;
  reason?: PlaceholderNoMatchReason;
  source?: PlaceholderSource;
}

export interface ResolvedPlaceholdersResponse {
  placeholders: ResolvedPlaceholder[];
}

/** A merge field swapped for a sentinel while the template was with the AI, so it can be rebuilt. */
export interface PreservedField {
  sentinel: string;
  fieldName: string;
}

export interface AiGenerateApiResponse {
  sfdt: string;
  preservedFields: PreservedField[];
  lostImages?: number;
}

export interface AiGenerateResult {
  sfdt: string;
  preservedFields: PreservedField[];
  lostImages: number;
}

/** A field ready to insert: the merge key, and the text shown in its place in the editor. */
export interface MergeFieldSpec {
  fieldName: string;
  displayPath: string;
}

/**
 * One placeholder in the review; `fields` holds only fields already validated against the tree, and
 * `match` is null until the placeholder has been matched with AI.
 */
export interface FieldReviewRow {
  token: string;
  searchText: string;
  match: PlaceholderMatch | null;
  reason?: PlaceholderNoMatchReason;
  source?: PlaceholderSource;
  proposedFieldNames?: string[];
  fields: MergeFieldSpec[];
  selected: boolean;
  occurrences: number;
  overridden: boolean;
}

export type FieldReviewState = 'idle' | 'loading' | 'ready' | 'failed';

/**
 * One applied placeholder, sent back so the choice is proposed again next time. REJECTED carries the
 * fields memory proposed when the user changed them.
 */
export interface FieldAssociation {
  token: string;
  fieldNames: string[];
  outcome: 'ACCEPTED' | 'OVERRIDDEN' | 'REJECTED';
  source: PlaceholderSource | 'NONE';
}

export type InsertFieldTab = 'fields' | 'placeholders';

export interface SignatureRoleEntry {
  role: string;
  signatureIndex: number;
  imageDataUri: string;
  fingerprints: string[];
  signatureMethods: SignatureMethodEnum[];
}

export interface ETemplateFilterFields {
  name: string;
  folder: string;
  locale: string;
  date: string;
  statuses: string;
}

export interface TreeViewItem {
  id: string;
  parentID?: string;
  displayText?: string;
  transIds?: string[];
  hasChildren?: boolean;
}

export interface FieldItem {
  name: string;
  transId: string;
  fieldType: FieldType;
  metaData?: TreeNode<TreeItem>[];
  path?: string;
}

export interface MetaDataItem {
  display: string;
  value: string;
  selectable?: boolean;
}

export interface InsertConditionFormData {
  fieldName: string;
  operator: string;
  valueToCompare: string;
  trueResult: string;
  falseResult?: string;
}

export enum InsertType {
  INSERT_FIELDS = 'InsertFields',
  INSERT_CONDITIONS = 'InsertConditions'
}

/** A file field is only merged as an image when its merge key carries this prefix. */
export const IMAGE_FIELD_PREFIX = 'Image:';
